import { useCallback, useEffect, useRef, useState } from 'react';

export const PRESENTATION_SYNC_EVENT = 'presentation:state';
export const PRESENTATION_CLEARED_EVENT = 'presentation:cleared';
export const PRESENTATION_KINDS = Object.freeze([
  'ppt', 'pptx', 'pdf', 'doc', 'docx', 'txt', 'text', 'md', 'rtf',
  'xls', 'xlsx', 'csv', 'tsv', 'image',
]);

const cleanText = (value, maximum = 180) =>
  String(value || '').trim().slice(0, maximum);

const normalizeStatus = (status, state) => {
  if (['started', 'slide', 'stopped'].includes(status)) return status;
  if (state === 'started' || state === 'stopped') return state;
  return state === 'updated' ? 'slide' : 'slide';
};

const cleanDeckId = (value) =>
  cleanText(value, 100).replace(/[^A-Za-z0-9_-]/g, '');

const isUnsafeFileName = (value) => {
  const fileName = cleanText(value, 500);
  return (
    /[\\/]/.test(fileName) ||
    /^(?:https?:|blob:|data:)/i.test(fileName)
  );
};

export const normalizePresentationRelay = (payload = {}) => {
  const status = normalizeStatus(payload.status, payload.state);
  const kind = cleanText(payload.kind, 20).toLowerCase();
  const rawSentAt = payload.sentAt || payload.emittedAt;
  const sentAt = Number(rawSentAt) || Date.parse(String(rawSentAt || '')) || Date.now();
  return {
    roomId: cleanText(payload.roomId, 100),
    presenterSessionId: cleanText(payload.presenterSessionId, 100),
    presenterName: cleanText(payload.presenterName || payload.displayName, 100),
    userId: cleanText(payload.userId, 100),
    socketId: cleanText(payload.socketId, 100),
    role: cleanText(payload.role, 60),
    deckId: cleanDeckId(payload.deckId || payload.presentationId),
    fileName: cleanText(payload.fileName, 180),
    kind: PRESENTATION_KINDS.includes(kind) ? kind : '',
    slideIndex: Math.max(0, Math.floor(Number(payload.slideIndex) || 0)),
    slideCount: Math.max(0, Math.floor(Number(payload.slideCount) || 0)),
    status,
    version: Math.max(0, Math.floor(Number(payload.version) || 0)),
    sentAt,
  };
};

export const buildCanonicalPresentationPayload = ({
  roomId,
  values = {},
  version = 0,
} = {}) => {
  const canonicalRoomId = cleanText(roomId, 100);
  const deckId = cleanDeckId(values.deckId);
  const rawFileName = cleanText(values.fileName, 500);
  if (!canonicalRoomId) return { error: 'The meeting room is missing.' };
  if (!deckId) return { error: 'The local presentation identifier is invalid.' };
  if (rawFileName && isUnsafeFileName(rawFileName)) {
    return { error: 'The presentation file name contains an unsafe path or URL.' };
  }

  const status = normalizeStatus(values.status, values.state);
  const requestedKind = cleanText(values.kind, 20).toLowerCase();
  const kind = PRESENTATION_KINDS.includes(requestedKind) ? requestedKind : '';
  const payload = {
    roomId: canonicalRoomId,
    status,
    deckId,
    slideIndex: Math.max(0, Math.floor(Number(values.slideIndex) || 0)),
    slideCount: Math.max(0, Math.floor(Number(values.slideCount) || 0)),
    version: Math.max(0, Math.floor(Number(version) || 0)),
  };
  if (rawFileName) payload.fileName = rawFileName.slice(0, 180);
  if (kind) payload.kind = kind;
  if (values.title) payload.title = cleanText(values.title, 180);
  if (values.sourceId) payload.sourceId = cleanDeckId(values.sourceId);
  if (values.aspectRatio) payload.aspectRatio = cleanText(values.aspectRatio, 30);
  return { payload };
};

export const matchesPresentationClear = (presentation, cleared = {}) => {
  if (!presentation) return false;
  if (cleared.all === true) return true;
  const candidates = [
    [cleanDeckId(cleared.deckId || cleared.presentationId), presentation.deckId],
    [cleanText(cleared.presenterSessionId, 100), presentation.presenterSessionId],
    [cleanText(cleared.userId, 100), presentation.userId],
    [cleanText(cleared.socketId, 100), presentation.socketId],
  ].filter(([expected]) => Boolean(expected));
  return candidates.some(([expected, actual]) => expected === String(actual || ''));
};

export const presentationRelayKey = (presentation = {}) =>
  `${cleanText(presentation.presenterSessionId, 100)}:${cleanDeckId(presentation.deckId)}`;

export const isSamePresentationRelay = (current, incoming) => {
  if (!current || !incoming) return false;
  const currentSession = cleanText(current.presenterSessionId, 100);
  const incomingSession = cleanText(incoming.presenterSessionId, 100);
  const currentDeck = cleanDeckId(current.deckId);
  const incomingDeck = cleanDeckId(incoming.deckId);
  return Boolean(
    currentSession && incomingSession && currentSession === incomingSession &&
    currentDeck && incomingDeck && currentDeck === incomingDeck
  );
};

export const isNewerPresentationRelay = (incoming, previous) => {
  if (!previous) return true;
  const incomingSentAt = Number(incoming?.sentAt) || 0;
  const previousSentAt = Number(previous?.sentAt) || 0;
  if (incomingSentAt && previousSentAt && incomingSentAt !== previousSentAt) {
    return incomingSentAt > previousSentAt;
  }
  const incomingVersion = Math.max(0, Math.floor(Number(incoming?.version) || 0));
  const previousVersion = Math.max(0, Math.floor(Number(previous?.version) || 0));
  if (incomingVersion !== previousVersion) return incomingVersion > previousVersion;
  const rank = { started: 1, slide: 2, stopped: 3 };
  return (rank[incoming?.status] || 0) > (rank[previous?.status] || 0);
};

// This hook intentionally synchronizes presentation metadata only. Local file
// bytes and blob URLs never leave the presenter; participants see the rendered
// slide through the meeting's existing tab/window screen-share track.
export const usePresentationSync = ({
  socket,
  roomId,
  sessionId,
  canPublish = false,
}) => {
  const [remotePresentation, setRemotePresentation] = useState(null);
  const [presentationSyncError, setPresentationSyncError] = useState('');
  const [presentationClearRevision, setPresentationClearRevision] = useState(0);
  const versionRef = useRef(0);
  const lastIncomingRef = useRef('');
  const latestIncomingRef = useRef(new Map());
  const clearedTombstonesRef = useRef([]);
  const acknowledgementTimersRef = useRef(new Set());

  useEffect(() => {
    if (!socket) return undefined;
    const handleState = (raw = {}) => {
      const next = normalizePresentationRelay(raw);
      if (!next.roomId || next.roomId !== String(roomId || '')) return;
      if (next.presenterSessionId === String(sessionId || '')) return;
      if (!next.presenterSessionId || !next.deckId) return;
      const matchingTombstones = clearedTombstonesRef.current.filter((tombstone) =>
        matchesPresentationClear(next, tombstone)
      );
      if (matchingTombstones.length && next.status !== 'started') return;
      if (matchingTombstones.length) {
        clearedTombstonesRef.current = clearedTombstonesRef.current.filter((tombstone) =>
          !matchesPresentationClear(next, tombstone)
        );
      }
      const relayKey = presentationRelayKey(next);
      const previousOrder = latestIncomingRef.current.get(relayKey);
      if (!isNewerPresentationRelay(next, previousOrder)) return;
      latestIncomingRef.current.set(relayKey, next);
      const dedupeKey = `${relayKey}:${next.version}:${next.status}:${next.slideIndex}:${next.sentAt}`;
      if (lastIncomingRef.current === dedupeKey) return;
      lastIncomingRef.current = dedupeKey;
      setRemotePresentation((current) => {
        if (next.status !== 'stopped') return next;
        return !current || isSamePresentationRelay(current, next) ? null : current;
      });
    };
    const handleCleared = (raw = {}) => {
      if (String(raw.roomId || '') !== String(roomId || '')) return;
      clearedTombstonesRef.current = [
        ...clearedTombstonesRef.current.slice(-99),
        raw.all === true ? { all: true } : raw,
      ];
      if (raw.all === true) {
        lastIncomingRef.current = '';
        latestIncomingRef.current.clear();
        setRemotePresentation(null);
        setPresentationSyncError('');
        setPresentationClearRevision((value) => value + 1);
        return;
      }
      setRemotePresentation((current) => {
        if (!matchesPresentationClear(current, raw)) return current;
        lastIncomingRef.current = '';
        return null;
      });
    };
    socket.on(PRESENTATION_SYNC_EVENT, handleState);
    socket.on(PRESENTATION_CLEARED_EVENT, handleCleared);
    return () => {
      socket.off(PRESENTATION_SYNC_EVENT, handleState);
      socket.off(PRESENTATION_CLEARED_EVENT, handleCleared);
    };
  }, [roomId, sessionId, socket]);

  useEffect(() => () => {
    acknowledgementTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    acknowledgementTimersRef.current.clear();
  }, []);

  const publishPresentationState = useCallback((values = {}) => {
    if (!canPublish) {
      setPresentationSyncError('Only the active presenter or an admitted moderator can synchronize slides.');
      return false;
    }
    if (!socket?.connected || !roomId) {
      setPresentationSyncError('Slide state could not be synchronized while the meeting is reconnecting.');
      return false;
    }
    versionRef.current += 1;
    const requestVersion = versionRef.current;
    const { payload, error } = buildCanonicalPresentationPayload({
      roomId,
      values,
      version: requestVersion,
    });
    if (error) {
      setPresentationSyncError(error);
      return false;
    }

    setPresentationSyncError('');
    const timer = window.setTimeout(() => {
      acknowledgementTimersRef.current.delete(timer);
      if (versionRef.current !== requestVersion) return;
      setPresentationSyncError('The meeting server did not confirm slide synchronization. Screen sharing continues normally.');
    }, 8000);
    acknowledgementTimersRef.current.add(timer);
    socket.emit(PRESENTATION_SYNC_EVENT, payload, (response = {}) => {
      window.clearTimeout(timer);
      acknowledgementTimersRef.current.delete(timer);
      if (versionRef.current !== requestVersion) return;
      if (response.success === false || response.error) {
        setPresentationSyncError(
          cleanText(response.error || response.message, 220) ||
            'The meeting server rejected slide synchronization.'
        );
        return;
      }
      if (response.throttled === true) {
        setPresentationSyncError('Slide synchronization is updating too quickly. The next settled slide will be relayed.');
        return;
      }
      setPresentationSyncError('');
    });
    return true;
  }, [canPublish, roomId, socket]);

  return {
    remotePresentation,
    presentationClearRevision,
    presentationSyncError,
    publishPresentationState,
  };
};

export default usePresentationSync;
